import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiZap, FiArrowRight, FiMessageSquare, FiEdit3,
  FiClock, FiCheckCircle
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const AIDocs = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/ai" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">AI Assistance</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl mb-4">
              <FiZap className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">AI Assistance</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Leverage AI-powered features to work smarter, create tasks faster, and get intelligent suggestions.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Work Smarter with AI</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Tasklina's AI assistant helps you be more productive by automating routine tasks, 
                suggesting improvements, and helping you write better task descriptions.
              </p>
              <div className="inline-block bg-white dark:bg-gray-800 rounded-lg px-4 py-2">
                <span className="text-sm text-purple-600 font-medium">✨ Powered by advanced AI</span>
              </div>
            </div>
          </section>

          {/* AI Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">AI-Powered Features</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FiMessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Chat Assistant</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Chat with the AI assistant to quickly create tasks, get help with project planning, 
                  or ask questions about your work.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                  <li>• "Create a task to review the marketing proposal"</li>
                  <li>• "What tasks are due this week?"</li>
                  <li>• "Help me break down this project into tasks"</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FiEdit3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Smart Task Writing</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Get AI suggestions to improve your task descriptions, making them clearer 
                  and more actionable.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                  <li>• Improve task clarity</li>
                  <li>• Add missing details</li>
                  <li>• Suggest acceptance criteria</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <FiClock className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Time Estimation</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  AI analyzes similar past tasks to suggest realistic time estimates for new tasks.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                  <li>• Based on historical data</li>
                  <li>• Considers task complexity</li>
                  <li>• Improves over time</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Using AI Assistant */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Using the AI Assistant</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <ol className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                  <span>Click the AI Assistant button (✨) in the bottom right</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                  <span>Type your request or question in natural language</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                  <span>Review the AI's response and suggested actions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                  <span>Apply suggestions with one click or modify as needed</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Example Commands */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Example Commands</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-200 font-mono">"Create 5 tasks for website redesign"</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-200 font-mono">"Summarize my overdue tasks"</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-200 font-mono">"Help me write a task description"</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-200 font-mono">"What should I focus on today?"</p>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Best Practices</h2>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Be specific in your requests for better results</span>
              </li>
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Always review AI suggestions before applying</span>
              </li>
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Use AI for inspiration, not as the final word</span>
              </li>
              <li className="flex items-start space-x-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-200">Provide context when asking for project breakdowns</span>
              </li>
            </ul>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/projects"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Project Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default AIDocs;
